/**
 * 計画詳細の 48 コマの表 (描画済みの `<table>`) を「行 × 列の矩形」として読む純関数群
 * (`docs/specs/conventions.md`「Excelライクなグリッド編集」の範囲選択・コピー、
 * `docs/specs/plan-submit/plan-common.md` §2 の列ヘッダーでの列の選択)。
 * conventions は対象を計画詳細に限るので、`PlanTable` 専用に置く。
 *
 * React 版 mock の `TableRangeCopy` と同じく **DOM をそのまま読む**。画面に見えている
 * 文字列 (見出し・値・日量合計) をそのままコピーするので、画面とコピーの値が食い違わない。
 * 座標は表示上の 0 始まり index で、矩形の型と TSV の組み立ては `~/models/gridSelection`
 * を共有する。DOM 副作用は [[useTableRangeCopy]] が持ち、ここには計算だけを置く。
 */
import { type CellPos, type CellRange, buildTsv } from '~/models/gridSelection'

/** 選択中セルの目印。ハイライトの CSS は `~/constants/system` の globalCss に 1 度だけ置く。 */
export const RANGE_SELECTED_ATTR = 'data-range-selected'

/** ドラッグ中コンテナの目印 (ネイティブのテキスト選択帯と二重に出さない)。 */
export const RANGE_DRAGGING_ATTR = 'data-range-dragging'

/**
 * 押すとその列を選ぶ列見出し (`<th>`) の目印 (plan-common §2)。
 *
 * 付けるのは 1 列だけを指す見出し (群見出しの下の段・rowSpan で最下段まで届く見出し)。
 * 見出しにリンク・アイコン・並べ替えなどの操作がある列には付けない (その列は範囲選択で
 * コピーする)。
 */
export const RANGE_COLUMN_HEADER_ATTR = 'data-range-column-header'

/**
 * 列見出しでの列の選択に含めない本体の行 (`<tr>`) の目印。
 * 計画詳細の日量合計行のように、列の選択 (= 48 コマ) の外に置く行に付ける。
 * 範囲選択では選べる。
 */
export const RANGE_COLUMN_EXCLUDE_ROW_ATTR = 'data-range-column-exclude'

/** grid の 1 マス。`rowOffset` / `colOffset` は rowspan / colspan の起点からの距離。 */
type GridSlot = {
  cell: HTMLTableCellElement
  rowOffset: number
  colOffset: number
}

export type TableRangeModel = {
  table: HTMLTableElement
  /**
   * 見出し (`<thead>`) の行 → 本体 (`<tbody>`) の行の順に並べたセル。空きマスは `undefined`。
   * 見出しのセルも範囲選択で選べる (選んだ時だけコピーに入る。#9700 で利用者確認済み)。
   */
  grid: Array<Array<GridSlot | undefined>>
  rowCount: number
  colCount: number
  /**
   * 列見出しで列を選ぶ時の行範囲 ([[RANGE_COLUMN_EXCLUDE_ROW_ATTR]] の行を除いた
   * 先頭〜末尾)。対象の行が無ければ `null`。
   */
  columnRows: { rowMin: number; rowMax: number } | null
  /** DOM セル → 座標 (span の起点座標)。対象外セルは `null`。 */
  posOf: (cell: Element) => CellPos | null
  /** 列見出し ([[RANGE_COLUMN_HEADER_ATTR]] 付きの `<th>`) → 列 index。対象外は `null`。 */
  columnOf: (header: Element) => number | null
}

/*
 * DOM の読み出しは `HTMLTableElement.tBodies` / `.rows` / `.cells` のような
 * table 専用 API ではなく querySelector で行う。テスト環境 (happy-dom) が
 * これらを実装しておらず、実機とテストで挙動が割れるため。
 */

/** section (thead / tbody) 直下の行。 */
function sectionRows(section: Element): HTMLTableRowElement[] {
  return Array.from(
    section.querySelectorAll<HTMLTableRowElement>(':scope > tr'),
  )
}

/** 行の直下のセル。 */
function rowCells(row: Element): HTMLTableCellElement[] {
  return Array.from(
    row.querySelectorAll<HTMLTableCellElement>(':scope > td, :scope > th'),
  )
}

/** span 属性 (未実装環境では属性値から読む)。 */
function cellSpan(cell: HTMLTableCellElement, name: 'col' | 'row'): number {
  const raw =
    (name === 'col' ? cell.colSpan : cell.rowSpan) ??
    Number(cell.getAttribute(`${name}span`))
  return Number.isFinite(raw) && raw > 0 ? Math.trunc(raw) : 1
}

/** `<tr>` の並びを colspan / rowspan 込みの矩形 grid に展開する。 */
function buildCellGrid(
  rows: HTMLTableRowElement[],
): Array<Array<GridSlot | undefined>> {
  const grid: Array<Array<GridSlot | undefined>> = rows.map(() => [])
  for (const [rowIndex, row] of rows.entries()) {
    const current = grid[rowIndex]
    if (current === undefined) continue
    let col = 0
    for (const cell of rowCells(row)) {
      // 上の行から伸びてきた rowspan の占有マスを飛ばす
      while (current[col] !== undefined) col += 1
      const colSpan = cellSpan(cell, 'col')
      const rowSpan = cellSpan(cell, 'row')
      for (let dr = 0; dr < rowSpan && rowIndex + dr < rows.length; dr += 1) {
        const target = grid[rowIndex + dr]
        if (target === undefined) continue
        for (let dc = 0; dc < colSpan; dc += 1) {
          target[col + dc] = { cell, rowOffset: dr, colOffset: dc }
        }
      }
      col += colSpan
    }
  }
  return grid
}

/** テキストとして拾わない要素 (装飾・スクリプト)。 */
const TEXT_SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'SVG'])

/**
 * セルの表示文字列 (`innerText` 相当)。
 *
 * 複数要素が入るセル (取引先コード + 取引先名の 2 段見出し等) は**空白で結合**し、
 * セル内の改行は空白へ潰す (TSV の行構造を壊さない)。`innerText` を直接使わないのは、
 * happy-dom で未実装だとテストと実機で結果がズレるため。
 */
export function extractCellText(cell: Element): string {
  const parts: string[] = []
  const walk = (node: Node): void => {
    if (node.nodeType === node.TEXT_NODE) {
      const text = (node.textContent ?? '').trim()
      if (text !== '') parts.push(text)
      return
    }
    if (node.nodeType !== node.ELEMENT_NODE) return
    const element = node as Element
    if (element.getAttribute('aria-hidden') === 'true') return
    if (TEXT_SKIP_TAGS.has(element.tagName.toUpperCase())) return
    for (const child of Array.from(element.childNodes)) walk(child)
  }
  walk(cell)
  return parts.join(' ').replace(/\s+/g, ' ').trim()
}

/** 桁区切りカンマだけで構成された数値 (`-1,234.5` / `+12,345`)。 */
const THOUSANDS_SEPARATED = /^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/

/** 表示用の負号「−」(U+2212) で始まる数値 (`−1,234` / `−5`)。 需給バランスの負の値 (AC-PLC-012)。 */
const DISPLAY_MINUS_NUMBER = /^\u2212\d[\d,]*(?:\.\d+)?$/

/**
 * コピー時の値の正規化。
 *
 * **数値セルだけ**桁区切りカンマを外し、 表示用の負号「−」(U+2212) を ASCII「-」 に戻して
 * 生値にする (`12,345` → `12345`、 `−1,234` → `-1234`)。
 * Excel 側で数値として扱えないと合計も並べ替えもできないため。
 * 単位・記号付きは**表示文字列のまま**にする (どこまでが数値かを推測して落とすと、
 * 単位の取り違えを生む)。 空セルの「−」 単独は数値ではないので変えない。
 */
export function normalizeCopyText(text: string): string {
  const ascii = DISPLAY_MINUS_NUMBER.test(text) ? `-${text.slice(1)}` : text
  return THOUSANDS_SEPARATED.test(ascii) ? ascii.replace(/,/g, '') : ascii
}

/** `<table>` から選択・コピーのためのモデルを読む (呼ぶたびに現在の DOM を見る)。 */
export function readTableRangeModel(table: HTMLTableElement): TableRangeModel {
  // span は section 内で閉じるので、見出しと本体は別々に展開してから縦につなぐ
  const headerGrid = buildCellGrid(
    Array.from(table.querySelectorAll(':scope > thead')).flatMap(sectionRows),
  )
  const bodyRows = Array.from(table.querySelectorAll(':scope > tbody')).flatMap(
    sectionRows,
  )
  const bodyGrid = buildCellGrid(bodyRows)
  const grid = [...headerGrid, ...bodyGrid]
  const colCount = Math.max(0, ...bodyGrid.map((row) => row.length))

  const positions = new Map<Element, CellPos>()
  for (const [rowIndex, row] of grid.entries()) {
    for (const [colIndex, slot] of row.entries()) {
      if (slot === undefined || slot.rowOffset !== 0 || slot.colOffset !== 0) {
        continue
      }
      positions.set(slot.cell, { row: rowIndex, col: colIndex })
    }
  }

  // 列見出しは 1 列だけを指す (colSpan 1) ものだけを列に対応づける。
  const headerColumns = new Map<Element, number>()
  for (const row of headerGrid) {
    for (const [colIndex, slot] of row.entries()) {
      if (
        slot === undefined ||
        slot.rowOffset !== 0 ||
        cellSpan(slot.cell, 'col') !== 1 ||
        !slot.cell.hasAttribute(RANGE_COLUMN_HEADER_ATTR)
      ) {
        continue
      }
      headerColumns.set(slot.cell, colIndex)
    }
  }

  // 列の選択は本体の行だけ (見出しは含めない。spec の「その列の全行 (48 コマ)」)
  let columnRows: TableRangeModel['columnRows'] = null
  for (const [bodyIndex, row] of bodyRows.entries()) {
    if (row.hasAttribute(RANGE_COLUMN_EXCLUDE_ROW_ATTR)) continue
    const rowIndex = headerGrid.length + bodyIndex
    columnRows =
      columnRows === null
        ? { rowMin: rowIndex, rowMax: rowIndex }
        : { rowMin: columnRows.rowMin, rowMax: rowIndex }
  }

  return {
    table,
    grid,
    rowCount: grid.length,
    colCount,
    columnRows,
    posOf: (cell) => positions.get(cell) ?? null,
    columnOf: (header) => {
      const col = headerColumns.get(header)
      return col !== undefined && col < colCount ? col : null
    },
  }
}

/**
 * 範囲を今のモデルの中へ収める (選択後に列・行が減った時のコピー対策)。
 * 収まる行 / 列が 1 つも無ければ `null`。
 */
export function clampRange(
  range: CellRange,
  model: TableRangeModel,
): CellRange | null {
  if (model.rowCount === 0 || model.colCount === 0) return null
  return {
    rowMin: Math.min(range.rowMin, model.rowCount - 1),
    rowMax: Math.min(range.rowMax, model.rowCount - 1),
    colMin: Math.min(range.colMin, model.colCount - 1),
    colMax: Math.min(range.colMax, model.colCount - 1),
  }
}

/**
 * 選択範囲を Excel へ貼れる TSV にする。
 *
 * **選んだセルだけ**を出す。見出しは選択範囲に見出しのセルを含めた時だけ入る
 * (React 版 mock は見出しを常に付けるが、選んでいない見出しを付けないよう #9700 で
 * 利用者確認済み)。
 */
export function buildRangeTsv(
  model: TableRangeModel,
  range: CellRange,
): string {
  const lines: string[][] = []
  for (let row = range.rowMin; row <= range.rowMax; row += 1) {
    const cols: string[] = []
    for (let col = range.colMin; col <= range.colMax; col += 1) {
      const slot = model.grid[row]?.[col]
      if (slot === undefined) {
        cols.push('')
        continue
      }
      // 結合セルの値は**選択範囲の中の左上 1 箇所**にだけ置く (同じ値を横に並べない)
      const isSpanOrigin =
        (slot.colOffset === 0 || col === range.colMin) &&
        (slot.rowOffset === 0 || row === range.rowMin)
      cols.push(
        isSpanOrigin ? normalizeCopyText(extractCellText(slot.cell)) : '',
      )
    }
    lines.push(cols)
  }
  return buildTsv(lines)
}
